import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, HostBinding, Input, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormControl,
  AbstractControl,
  ControlValueAccessor,
  Validator,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { Checkbox, Value } from '@platform500services/p500-ui-kit';
import { Subscription } from 'rxjs';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { CountryRegion, isCountryRegion } from '../../fields/default/country-region/types';
import { ICountries } from '../../../services/data/data-service.interface';
import { DataServiceConstants } from '../../../services/data/data.constants';

@Component({
  selector: 'gtd-questions',
  templateUrl: './questions.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QuestionsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => QuestionsComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@DestroySubscribers()
export class QuestionsComponent implements ControlValueAccessor, Validator, OnInit {
  public subscribers: Record<string, Subscription> = {};

  @Input()
  public set data(items: ICRMQuestionData[]) {
    this.initForm(items);
  }

  @Input() defaultQuestion: string;
  @HostBinding('class') className = 'c-modal--info';

  public questions: IMapCRMQuestionData[] = [];
  public form = new CRMQuestionFormArray([]);
  public selectedCountryRegions = new Map<number, CountryRegion>();
  public countries: ICountries;
  private cache = new Map();
  private onChange: (value: any) => void = () => {};
  private onTouch = () => {};

  constructor(private cdr: ChangeDetectorRef) {
    this.countries = DataServiceConstants.countries;
  }

  public initForm(items: ICRMQuestionData[]): void {
    this.questions = items.reduce<IMapCRMQuestionData[]>((acc, item) => {
      try {
        const value = Object.assign({}, item, { question: JSON.parse(item.question) });
        acc.push(value);
      } catch {}
      return acc;
    }, []);
    this.questions.forEach((item) => {
      const group = new CRMQuestionGroup({
        question_id: new UntypedFormControl(),
        answer: new CRMQuestionControl([], item.required ? [answerValidate] : []),
      });
      this.form.push(group);
    });
    this.form.patchValue(this.questions);
    this.form.updateValueAndValidity();
  }

  public writeValue(question: ICRMQuestionData[]): void {
    if (question.length > 0) {
      this.initForm(question);
      this.cdr.markForCheck();
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public validate(): ValidationErrors | null {
    return this.form.invalid ? { errors: true } : null;
  }

  public ngOnInit(): void {
    const toBackendFormat = (items: IMapCRMQuestionData[]) =>
      items.map((item) => Object.assign({}, item, { answer: JSON.stringify(item.answer) }));
    this.subscribers.form = this.form.valueChanges.subscribe((items) => {
      this.onChange(toBackendFormat(items));
      this.onTouch();
    });
    this.form.updateValueAndValidity();
  }

  public formChange(value: string, control: CRMQuestionControl): void {
    control.setValue(value);
  }

  public countryRegionChange(value: CountryRegion, control: CRMQuestionControl, questionId: number): void {
    this.selectedCountryRegions.set(questionId, value);
    control.setValue(value);
  }

  public checkboxChange(value: Value[], control: CRMQuestionControl): void {
    control.setValue(value.map(({ key }) => key));
  }

  public makeCheckboxData(items: string[]): Checkbox[] {
    const key = JSON.stringify(items);
    if (!this.cache.has(key)) {
      this.cache.set(
        key,
        items.map((key) => ({ key, label: key }))
      );
    }
    return this.cache.get(key);
  }

  public makeCheckboxValues(items: string[]): Value[] {
    const key = JSON.stringify(items);
    if (!this.cache.has(key)) {
      this.cache.set(
        key,
        items.map((key) => ({ key }))
      );
    }
    return this.cache.get(key);
  }
}

class CRMQuestionFormArray extends UntypedFormArray {
  controls: CRMQuestionGroup[];
}

class CRMQuestionGroup extends UntypedFormGroup {
  controls: {
    question_id: UntypedFormControl;
    answer: CRMQuestionControl;
  };
}

class CRMQuestionControl extends UntypedFormControl {
  setValue(
    value: any,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
      emitModelToViewChange?: boolean;
      emitViewToModelChange?: boolean;
    }
  ): void {
    super.setValue(Array.isArray(value) ? value : [value], options);
  }
}

const answerValidate = (control: AbstractControl) => {
  if (Array.isArray(control.value) && control.value.length) {
    const values = control.value.map((value) => {
      if (isCountryRegion(value)) {
        return value.country_code;
      }
      return value;
    });
    const filterValue = values.filter((value) => !!value);
    if (filterValue.length) {
      return null;
    }
  }
  return { customValidate: 'Field is Required' };
};

interface IMapCRMQuestionData {
  question_id: number;
  question: ICRMQuestion;
  required: boolean;
  answer: string;
}

export interface ICRMQuestionData {
  answer_id?: number;
  question_id: number;
  question: string;
  required: boolean;
  answer: string;
}

export interface ICRMQuestionsDataRequest {
  data: ICRMQuestionData[];
}

interface ICRMQuestion {
  question: string;
  type: string;
  answers: string[];
}
